import * as Yup from 'yup';
import { requiredValidator } from '../../forms/validators';
import { PinboardMessageFormModel } from '../models/PinboardMessageFormModel';

const isUnpublishAfterPublish = (values: PinboardMessageFormModel): boolean => {
    if (!values.date || !values.endDate) {
        return true;
    }

    // Let required validation handle missing time values.
    if ((values.date && !values.time) || (values.endDate && !values.endTime)) {
        return true;
    }

    const publishDateTime = new Date(values.date);
    const [publishHours, publishMinutes] = values.time!.split(':').map(Number);
    publishDateTime.setHours(publishHours, publishMinutes, 0, 0);

    const unpublishDateTime = new Date(values.endDate);
    const [unpublishHours, unpublishMinutes] = values.endTime!.split(':').map(Number);
    unpublishDateTime.setHours(unpublishHours, unpublishMinutes, 0, 0);

    return unpublishDateTime > publishDateTime;
};

export const pinboardMessageValidationSchema = Yup.object().shape({
    title: requiredValidator.max(30, 'Ämnet får vara max 30 tecken.'),
    text: requiredValidator.max(10000, 'Meddelandet får vara max 10 000 tecken.'),
    canUserAnswer: Yup.string()
        .nullable()
        .matches(/ja|nej/, 'Ogiltigt värde')
        .required('Välj om meddelandet skall gå att svara på'),
    target: Yup.string().nullable().required('Välj mottagare'),
    targetAssociations: Yup.array().when('target', {
        is: 'some',
        then: (schema) => schema.required('Välj minst ett engagemang').min(1, 'Välj minst ett engagemang'),
    }),
    date: Yup.date()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .nullable()
        .typeError('Publiceringsdatum måste vara ett giltigt datum')
        .test('today-or-future', 'Publiceringsdatum kan inte vara i det förflutna', (value) => {
            if (!value) {
                return true;
            }

            const selectedDate = new Date(value);
            selectedDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return selectedDate >= today;
        }),
    time: Yup.string()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .nullable()
        .matches(/^[0-2][0-9]:[0-5][0-9]$/, 'Tid måste vara i formatet HH:MM')
        .when('date', {
            is: (date: any) => date !== null && date !== undefined,
            then: (schema) => schema.required('Publiceringstid är obligatorisk när publiceringsdatum är angivet'),
        }),
    endDate: Yup.date()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .nullable()
        .typeError('Avpubliceringsdatum måste vara ett giltigt datum')
        .test('after-publish-date', 'Avpubliceringsdatum måste vara efter publiceringsdatum', function () {
            return isUnpublishAfterPublish(this.parent as PinboardMessageFormModel);
        }),
    endTime: Yup.string()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .nullable()
        .matches(/^[0-2][0-9]:[0-5][0-9]$/, 'Tid måste vara i formatet HH:MM')
        .when('endDate', {
            is: (endDate: any) => endDate !== null && endDate !== undefined,
            then: (schema) => schema.required('Publiceringstid är obligatorisk när avpubliceringsdatum är angivet'),
        })
        .test('after-publish-time', 'Avpubliceringsdatum måste vara efter publiceringsdatum', function () {
            return isUnpublishAfterPublish(this.parent as PinboardMessageFormModel);
        }),
});
